import { HIDE_CONTENTS_NOT_RU, HIDE_CONTENTS_RU } from '@/constants/hideContents';
import { SearchResult } from '@/templates/DataNames';
import { TreeData } from '@/templates/Tree';

const isFqdnSegmentMatch = (fqdnSource: string, fqdnTarget: string, separator: string): boolean => {
  const pattern = `(?:^|${separator})${fqdnTarget}(?:${separator}|$)`;
  const regex = new RegExp(pattern);
  return regex.test(fqdnSource);
};

export const shouldHideContent = (url: string): boolean => {
  return [...HIDE_CONTENTS_NOT_RU, ...HIDE_CONTENTS_RU].some((fqdn) =>
    isFqdnSegmentMatch(url, fqdn, '/')
  );
};

const getExcludedFqdnsByCountry = (userCountryCode: string): string[] =>
  userCountryCode === 'ru' ? HIDE_CONTENTS_RU : HIDE_CONTENTS_NOT_RU;

const normalizeExcludedFqdns = (excludedFqdns: string[]): string[] =>
  excludedFqdns.map((fqdn) => fqdn.replace(/\//g, '.'));

export const filterContentFromSearch = (
  searchResult: SearchResult,
  userCountryCode?: string
): SearchResult => {
  const excludedFqdns = getExcludedFqdnsByCountry(userCountryCode);
  const normalizedExcludedFqdns = normalizeExcludedFqdns(excludedFqdns);

  const filteredSearchResult = searchResult.results.map((cat) => {
    return {
      ...cat,
      results: cat.results.filter((searchItem) =>
        normalizedExcludedFqdns.every(
          (excludedFqdn) => !isFqdnSegmentMatch(searchItem.fqdn, excludedFqdn, '/')
        )
      ),
    };
  });

  return { ...searchResult, results: filteredSearchResult };
};

export const filterContentFromFullContent = (
  node: TreeData,
  userCountryCode?: string
): TreeData => {
  const excludedFqdns = getExcludedFqdnsByCountry(userCountryCode);
  const normalizedExcludedFqdns = normalizeExcludedFqdns(excludedFqdns);

  return {
    ...node,
    ...(node.children
      ? {
          children: node.children.filter((childNode) =>
            normalizedExcludedFqdns.every(
              (excludedFqdn) => !isFqdnSegmentMatch(childNode.fqdn, excludedFqdn, '/')
            )
          ),
        }
      : {}),
  };
};

export const filterContentFromTree = (
  treeNodes: TreeData[],
  userCountryCode?: string
): TreeData[] => {
  const excludedFqdns = getExcludedFqdnsByCountry(userCountryCode);
  const normalizedExcludedFqdns = normalizeExcludedFqdns(excludedFqdns);

  const recursivelyFilter = (nodes: TreeData[]): TreeData[] =>
    nodes
      .filter((node) =>
        normalizedExcludedFqdns.every(
          (excludedFqdn) => !isFqdnSegmentMatch(node.fqdn, excludedFqdn, '/')
        )
      )
      .map((node) => ({
        ...node,
        ...(node.children ? { children: recursivelyFilter(node.children) } : {}),
      }));

  return recursivelyFilter(treeNodes);
};
